import React, { useState, useEffect } from "react";
import AOS from "aos";
import SEO from "../components/SEO";
import Hero from "../components/Hero";
import API from "../api";

const slidesData = [
  {
    id: 1,
    image: "/assets/img/banner2.png",
    title: "Contact Us",
    subtitle: "We'd love to hear from you — questions, feedback or partnership enquiries.",
    buttonText: "Get in touch",
    buttonLink: "#contact-form",
  },
];

const ContactPage = () => {
  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  // Ensure the page scrolls to top whenever this page is navigated to or the id changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window?.scrollTo) {
        // instant jump to top to avoid flashing at previous scroll position
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      // fallback: simple assignment
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        // ignore in environments without window
      }
    }
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    type: "academic_enquiries",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastPayload, setLastPayload] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    if (!form.type.trim()) err.type = "Type is required";
    if (!form.subject.trim()) err.subject = "Subject is required";
    if (!form.message.trim()) err.message = "Message is required";
    return err;
  };

  const extractSubmitError = (err, fallbackMsg) => {
    const data = err?.response?.data || {};
    const msg = data?.message || err?.message || fallbackMsg;
    const reason = data?.reason ? ` Reason: ${data.reason}` : "";
    return `${msg}${reason}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setLoading(true);
    setSuccess(null);
    setErrors({});
    const payload = { ...form };
    setLastPayload({ body: payload });

    try {
      const res = await API.post("/contact", payload, {
        headers: { "Content-Type": "application/json" }
      });

      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || "Failed to send message");
      }
      const data = res.data;

      setSuccess(data.message || "Message sent successfully!");
      setForm({ name: "", email: "", phone: "", type: "academic_enquiries", subject: "", message: "" });
      setLastPayload(null);
    } catch (err) {
      console.error(err);
      const msg = extractSubmitError(
        err,
        "There was an error sending your message. Please try again."
      );
      setErrors({ submit: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload) return;
    setErrors({});
    setSuccess(null);
    setLoading(true);

    try {
      const res = await API.post("/contact", lastPayload.body, {
        headers: { "Content-Type": "application/json" }
      });

      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || "Failed to send message");
      }

      const data = res.data;
      setSuccess(data.message || "Message sent successfully!");
      setForm({ name: "", email: "", phone: "", type: "academic_enquiries", subject: "", message: "" });
      setLastPayload(null);
    } catch (err) {
      console.error(err);
      const msg = extractSubmitError(
        err,
        "There was an error submitting your enquiry. Please try again."
      );
      setErrors((s) => ({ ...s, submit: msg }));
    } finally {
      setLoading(false);
    }
  };


  return (
    <section className="pt-1 pb-16 bg-gray-100 min-h-screen">
      <SEO 
        title="Contact — Africa Research University" 
        description="Get in touch with Africa Research University for enquiries, admissions and partnerships."
        url="/contact"
        breadcrumbs={[
          { name: 'Home', url: 'https://africaresearchuniversity.com' },
          { name: 'Contact', url: 'https://africaresearchuniversity.com/contact' }
        ]}
      />
      <Hero slides={slidesData} interval={6000} height="260px" borderRadius="20px" hidden={true} />

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Form column */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow p-6" id="contact-form" data-aos="fade-up">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Send us a message</h2>
            <p className="text-sm text-gray-600 mb-6">Fill out the form and we'll respond as soon as possible.</p>

            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">{success}</div>
            )}

            {errors.submit && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                <div className="font-medium">Submission error</div>
                <div className="text-sm mt-1">{errors.submit}</div>
                <div className="mt-2">
                  <button type="button" onClick={handleRetry} disabled={loading} className="text-sm underline text-red-700 hover:text-red-900">Retry</button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full name *</label>
                  <input name="name" value={form.name} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm" />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email *</label>
                  <input name="email" value={form.email} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm" />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone (optional)</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Type *</label>
                <select name="type" value={form.type} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm">
                  <option value="academic_enquiries">Academic Enquiry</option>
                  <option value="business_enquiries">Business Enquiry</option>
                </select>
                {errors.type && <p className="text-xs text-red-600 mt-1">{errors.type}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Subject *</label>
                <input name="subject" value={form.subject} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm" />
                {errors.subject && <p className="text-xs text-red-600 mt-1">{errors.subject}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Message *</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={6} className="mt-1 px-2 py-1 block w-full border rounded-md shadow-sm"></textarea>
                {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
              </div>

              <div className="flex items-center justify-between">
                <button type="submit" disabled={loading} className={`bg-green-600 text-white px-4 py-2 rounded-md transition ${loading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-green-700'}`}>
                  {loading ? 'Submitting...' : 'Send message'}
                </button>
                <button type="button" onClick={() => setForm({ name: "", email: "", phone: "", type: "academic_enquiries", subject: "", message: "" })} disabled={loading} className="text-sm text-gray-600 hover:text-gray-800">Reset</button>
              </div>
            </form>
          </div>

          {/* Contact info column */}
          <div className="bg-white rounded-lg shadow p-6" data-aos="fade-up" data-aos-delay="100">
            <h3 className="text-xl font-semibold mb-3">Contact Information</h3>

            <div className="mb-4">
              <h4 className="font-medium">Head Office</h4>
              <p className="text-sm text-gray-600">Quadrant Office Park<br />Great East Road<br />Lusaka, Zambia</p>
            </div>

            <div className="mb-4">
              <h4 className="font-medium">Phone</h4>
              <p className="text-sm text-gray-600">+260 773 029344</p>
            </div>

            <div className="mb-4">
              <h4 className="font-medium">Email</h4>
              <p className="text-sm text-gray-600">admissions@africaresearchuniversity.com</p>
            </div>

            <div className="mb-4">
              <h4 className="font-medium">Office Hours</h4>
              <p className="text-sm text-gray-600">Mon - Fri: 8:30am - 5:00pm</p>
            </div>

            <div className="mt-6">
              <h4 className="font-medium mb-2">Location</h4>
              <iframe
                title="Africa Research University Office Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3846.8419530393417!2d28.347959275867005!3d-15.385043485199608!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x193db96e68518b49%3A0xc451913bd14f7baf!2sAfrica%20Research%20University!5e0!3m2!1sen!2szm!4v1750324823305!5m2!1sen!2szm"
                className="w-full h-40 rounded border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPage;
